import { useIntl } from 'react-intl';
import { useMemo } from 'react';
import { InputNumber, Checkbox, Button, Label, Select, SelectOption } from 'ui';
import { ApplicationFiltersProps, ApplicationFilterState } from '../types';
import Filter from 'admin/components/Filter';
import { StyledFilters } from './styles';
import type { ListingProduct } from 'types/ListingData';

export const ApplicationFilters = ({
  onFinish,
  onOpenViewChange,
  openView,
  listing,
}: ApplicationFiltersProps) => {
  const intl = useIntl();

  const productCategories = useMemo(() => {
    if (!listing?.products) return [];

    const categories = new Map<number, string>();
    listing.products.forEach((product: ListingProduct) => {
      if (product.category?.id && product.category?.name) {
        categories.set(product.category.id, product.category.name);
      }
    });

    return Array.from(categories.entries()).map(([id, name]) => ({
      label: name,
      value: id,
    }));
  }, [listing]);

  const customParse = useMemo(
    () => ({
      selected_product_categories: (val: number[] | undefined) => {
        if (!val || !Array.isArray(val)) return val;
        return val.map((id) => {
          const category = productCategories.find((c) => c.value === id);
          return category?.label || String(id);
        });
      },
    }),
    [productCategories]
  );

  const handleFinish = (values: ApplicationFilterState) => {
    onFinish(values);
  };

  return (
    <Filter onFinish={handleFinish} isLabelVisible={true} customParse={customParse}>
      <StyledFilters>
        <div className="products-filter">
          <Label
            label={intl.formatMessage({
              id: 'listings.applications.filter.products',
            })}
          />
          <Select
            name="selected_product_categories"
            mode="multiple"
            allowClear
            showSearch
          >
            {productCategories.map((category) => (
              <SelectOption key={category.value} value={category.value}>
                {category.label}
              </SelectOption>
            ))}
          </Select>
        </div>
        <div className="price-filter">
          <Label
            label={intl.formatMessage({
              id: 'listings.application.total_price',
            })}
          />
          <div className="filter-input">
            <InputNumber
              name="price_from"
              placeholder={intl.formatMessage({
                id: 'general.from',
              })}
              min={0}
              rules={[
                ({ getFieldValue }) => ({
                  validator(_, value) {
                    const priceTo = getFieldValue('price_to');
                    const hasFrom = value != null && value !== '';
                    const hasTo = priceTo != null && priceTo !== '';
                    if (hasFrom && hasTo && value >= priceTo) {
                      return Promise.reject(new Error(''));
                    }
                    return Promise.resolve();
                  },
                }),
              ]}
            />
            <span>-</span>
            <InputNumber
              name="price_to"
              placeholder={intl.formatMessage({
                id: 'general.to',
              })}
              min={0}
              rules={[
                ({ getFieldValue }) => ({
                  validator(_, value) {
                    const priceFrom = getFieldValue('price_from');
                    const hasFrom = priceFrom != null && priceFrom !== '';
                    const hasTo = value != null && value !== '';
                    if (hasFrom && hasTo && value <= priceFrom) {
                      return Promise.reject(new Error(''));
                    }
                    return Promise.resolve();
                  },
                }),
              ]}
            />
          </div>
        </div>
        <div className="distance-filter">
          <Label
            label={intl.formatMessage({
              id: 'form.distance',
            })}
          />
          <div className="filter-input">
            <InputNumber
              name="distance_from"
              placeholder={intl.formatMessage({
                id: 'general.from',
              })}
              min={0}
              rules={[
                ({ getFieldValue }) => ({
                  validator(_, value) {
                    const distanceTo = getFieldValue('distance_to');
                    const hasFrom = value != null && value !== '';
                    const hasTo = distanceTo != null && distanceTo !== '';
                    if (hasFrom && hasTo && value >= distanceTo) {
                      return Promise.reject(new Error(''));
                    }
                    return Promise.resolve();
                  },
                }),
              ]}
            />
            <span>-</span>
            <InputNumber
              name="distance_to"
              placeholder={intl.formatMessage({
                id: 'general.to',
              })}
              min={0}
              rules={[
                ({ getFieldValue }) => ({
                  validator(_, value) {
                    const distanceFrom = getFieldValue('distance_from');
                    const hasFrom = distanceFrom != null && distanceFrom !== '';
                    const hasTo = value != null && value !== '';
                    if (hasFrom && hasTo && value <= distanceFrom) {
                      return Promise.reject(new Error(''));
                    }
                    return Promise.resolve();
                  },
                }),
              ]}
            />
          </div>
        </div>
        <Checkbox
          label={intl.formatMessage({
            id: 'listings.applications.filter.show_only_required_quantities',
          })}
          name="show_only_required_quantities"
        />
        <Checkbox
          className="open-view-checkbox"
          label={intl.formatMessage({
            id: 'listings.applications.filter.open_view',
          })}
          checked={openView}
          onChange={(e) => {
            onOpenViewChange(e.target.checked);
          }}
        />
      </StyledFilters>

      <Button
        type="primary"
        label={intl.formatMessage({ id: 'listings.apply_filter' })}
        htmlType="submit"
      />
    </Filter>
  );
};
